import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import COGSDetailPage from './pages/COGSDetailPage';
import COGSEditPage from './pages/COGSEditPage';
import AllCOGSRulesPage from './pages/AllCOGSRulesPage';

import PendingApprovalsPage from './pages/PendingApprovalsPage';
import DraftRulesPage from './pages/DraftRulesPage';
import AuditPage from './pages/AuditPage';
import LoadingSpinner from './components/Common/LoadingSpinner';
import MockAuth from './components/MockAuth';

// Admin pages
import CurrenciesPage from './pages/admin/CurrenciesPage';
import PricingSchemesPage from './pages/admin/PricingSchemesPage';
import ProductsPage from './pages/admin/ProductsPage';
import EntitiesPage from './pages/admin/EntitiesPage';
import ChannelsPage from './pages/admin/ChannelsPage';
import UsersPage from './pages/admin/UsersPage';

import './styles/App.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#1890ff',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    borderRadius: 6,
    fontSize: 14,
  },
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading } = useAuth();
  const userData = user?.data;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!userData) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && userData.role !== requiredRole && userData.role !== 'Admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Healthcheck Component
const HealthCheck = ({ type }) => {
  const response = {
    liveness: { status: "ALIVE", description: "Frontend application is running" },
    readiness: { status: "READY", description: "Frontend application is ready to serve requests" }
  };
  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', backgroundColor: '#f5f5f5' }}>
      <pre style={{ color: '#000', margin: 0 }}>
        {JSON.stringify(response[type], null, 2)}
      </pre>
    </div>
  );
};

// Main App Routes Component
const MainRoutes = () => {
  return (
    <Routes>
      <Route path="/healthcheck/liveness" element={<HealthCheck type="liveness" />} />
      <Route path="/healthcheck/readiness" element={<HealthCheck type="readiness" />} />
      <Route path="/*" element={
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      } />
    </Routes>
  );
};

// App Routes Component (now wrapped by AuthProvider)
const AppRoutes = () => {
  const { user, loading } = useAuth();
  const userData = user?.data;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!userData) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <MockAuth />
      </>
    );
  }

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cogs/:id" element={<COGSDetailPage />} />
          <Route path="/costing-rules" element={<AllCOGSRulesPage />} />
          <Route path="/costing-rules/:id/edit" element={<COGSEditPage />} />
          <Route path="/costing-rules/:id/edit-as-new" element={<COGSEditPage />} />

          <Route path="/pending-approvals" element={<PendingApprovalsPage />} />
          <Route path="/draft-rules" element={<DraftRulesPage />} />
          <Route
            path="/audit"
            element={
              <ProtectedRoute requiredRole="Admin">
                <AuditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/currencies"
            element={
              <ProtectedRoute requiredRole="Admin">
                <CurrenciesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pricing-schemes"
            element={
              <ProtectedRoute requiredRole="Admin">
                <PricingSchemesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute requiredRole="Admin">
                <ProductsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/entities"
            element={
              <ProtectedRoute requiredRole="Admin">
                <EntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/channels"
            element={
              <ProtectedRoute requiredRole="Admin">
                <ChannelsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="Admin">
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
      <MockAuth />
    </>
  );
};

// Main App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={theme}>
        <Router>
          <div className="App">
            <MainRoutes />
          </div>
        </Router>
      </ConfigProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}

export default App;
